import { Link, LinkProps } from "react-router-dom";
import { useCallback } from "react";

// Map of routes to their lazy import functions
const routeImports: Record<string, () => Promise<unknown>> = {
  "/": () => import("@/pages/Index"),
  "/financial-tinder": () => import("@/pages/FinancialTinderPage"),
  "/tinder": () => import("@/pages/FinancialTinderPage"),
  "/financial-league": () => import("@/pages/FinancialLeaguePage"),
  "/leaderboard": () => import("@/pages/LeaderboardPage"),
  "/community": () => import("@/pages/CommunityPage"),
  "/dashboard": () => import("@/pages/DashboardPage"),
  "/auth": () => import("@/pages/AuthPage"),
  "/profile": () => import("@/pages/ProfilePage"),
  "/professor": () => import("@/pages/ProfessorDashboard"),
  "/professor/create-batch": () => import("@/pages/CreateBatchPage"),
  "/rewards": () => import("@/pages/RewardsPage"),
  "/rewards-center": () => import("@/pages/RewardsPage"),
  "/analytics": () => import("@/pages/AnalyticsDashboard"),
  "/professor/content": () => import("@/pages/ContentManagement"),
  "/institution-admin": () => import("@/pages/InstitutionAdminDashboard"),
  "/ai-features": () => import("@/pages/AIFeaturesPage"),
  "/business-insights": () => import("@/pages/BusinessInsightsPage"),
  "/trading-journal": () => import("@/pages/TradingJournalPage"),
  "/interview-prep": () => import("@/pages/InterviewPrepPage"),
  "/help": () => import("@/pages/HelpPage"),
  "/faq": () => import("@/pages/HelpPage"),
};

// Cache to prevent duplicate prefetches
const prefetchedRoutes = new Set<string>();

// Prefetch function that can be called externally
export const prefetchRoute = (path: string) => {
  // Normalize path (remove query strings and hash)
  const normalizedPath = path.split("?")[0].split("#")[0];

  // Check for exact match first
  if (routeImports[normalizedPath] && !prefetchedRoutes.has(normalizedPath)) {
    prefetchedRoutes.add(normalizedPath);
    routeImports[normalizedPath]().catch(() => {
      // Allow retry if the chunk fetch fails (offline, transient network)
      prefetchedRoutes.delete(normalizedPath);
    });
    return;
  }

  // Check for dynamic routes
  const dynamicRoutes = [
    { pattern: /^\/community\/post\//, import: () => import("@/pages/CommunityPostPage") },
    { pattern: /^\/professor\/batch\//, import: () => import("@/pages/BatchDetailPage") },
    { pattern: /^\/profile\/[^/]+$/, import: () => import("@/pages/PublicProfilePage") },
  ];

  for (const route of dynamicRoutes) {
    if (route.pattern.test(normalizedPath) && !prefetchedRoutes.has(normalizedPath)) {
      prefetchedRoutes.add(normalizedPath);
      route.import().catch(() => {
        prefetchedRoutes.delete(normalizedPath);
      });
      return;
    }
  }
};

// Warm the most-likely-next routes during idle time after first paint.
export const prefetchLikelyRoutes = (paths: string[]) => {
  const run = () => paths.forEach(prefetchRoute);
  if (typeof window === "undefined") return;
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
  if (typeof ric === "function") {
    ric(run, { timeout: 2500 });
  } else {
    setTimeout(run, 1500);
  }
};

interface PrefetchLinkProps extends LinkProps {
  prefetch?: boolean;
}

export const PrefetchLink = ({
  to,
  prefetch = true,
  onMouseEnter,
  onFocus,
  children,
  ...props
}: PrefetchLinkProps) => {
  const handlePrefetch = useCallback(() => {
    if (prefetch && typeof to === "string") {
      prefetchRoute(to);
    }
  }, [to, prefetch]);

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    handlePrefetch();
    onMouseEnter?.(e);
  }, [handlePrefetch, onMouseEnter]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLAnchorElement>) => {
    handlePrefetch();
    onFocus?.(e);
  }, [handlePrefetch, onFocus]);

  return (
    <Link
      to={to}
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      {...props}
    >
      {children}
    </Link>
  );
};

export default PrefetchLink;
